/**
 * The mark on the kiosk link printed as the QR code on the lobby sign.
 *
 * A hunter who scans the sign is on their own phone, so the kiosk skips its
 * reset timers: there is no next hunter to protect, and clearing the form
 * while they are in another app finding a tag number only costs them their
 * work. Every other way in — the short address typed into the counter tablet,
 * or the same address typed off the sign by a hunter without a camera — has no
 * mark and keeps the timers.
 *
 * That default is on purpose. The page cannot tell a phone from a tablet, and
 * the costly mistake is a counter tablet that never resets and shows the next
 * hunter the last one's name, phone and cut sheet. A missing mark only means a
 * hunter gets a reset they did not need.
 *
 * Not a security control: deleting it from the link only turns the timers back
 * on for whoever deleted it.
 *
 * Shared between web and api so the sign and the page cannot drift apart.
 */
const KIOSK_SIGN_PARAM = 'from';
const KIOSK_SIGN_VALUE = 'sign';

/**
 * Add the sign's mark to a kiosk link.
 * @param kioskUrl - The full kiosk address.
 * @returns The address the sign's QR code should encode.
 */
export function withKioskSignMark(kioskUrl: string): string {
  const url = new URL(kioskUrl);
  url.searchParams.set(KIOSK_SIGN_PARAM, KIOSK_SIGN_VALUE);
  return url.toString();
}

/**
 * Did this visit come from scanning the sign — a hunter's own device?
 * @param searchParams - The kiosk page's query string.
 * @returns True when the reset timers should stay off.
 */
export function isKioskSignVisit(searchParams: URLSearchParams): boolean {
  return searchParams.get(KIOSK_SIGN_PARAM) === KIOSK_SIGN_VALUE;
}
